import { useEffect, useState } from 'react';
import { WebApiCustomListsDtoCustomListHistoryDto, WebApiModelsCustomListField } from '../../Repository/eventbookyapi';
import { AuthProvider } from '../../Providers/authProvider';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import { TextService } from '../../Services/TextService';
import LeftMenu from './LeftMenu';
import Table, { ColumnsType } from 'antd/lib/table';
import { formatDateString } from '../../Helpers/DateHelpers';
import { mapHistoryEventTypeToText } from '../../Mappers/mapHistoryEvents';
import { GetStringIfNotEmpty } from '../../Helpers/StringHelper';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import { CustomListService } from '../../Services/CustomListService';
import { CustomListFieldService } from '../../Services/CustomLifFieldService';

const CustomListHistory = () => {
    const [history, setHistory] = useState([] as WebApiCustomListsDtoCustomListHistoryDto[]);
    const [customListFields, setCustomListFields] = useState<Record<number, WebApiModelsCustomListField[]>>({});
    const [customLists, setCustomLists] = useState<any[]>([]);
    const [user, setUser] = useState({} as any);
    const GetText = TextService.GetText;

    useEffect(() => {
        AuthProvider().GetLoggedInUser().then(user => {
            setUser(user);
        });

        CustomListService.GetCustomListHistory().then(historyData => {
            setHistory(historyData);
        });

        // Load custom lists to get names
        CustomListService.GetCustomLists().then(lists => {
            setCustomLists(lists || []);

            // Load fields for each custom list
            const fieldsPromises = (lists || []).map((list: any) =>
                CustomListFieldService.GetCCustomListFields(list.id).then(fields => ({
                    listId: list.id,
                    fields: fields
                }))
            );

            Promise.all(fieldsPromises).then(results => {
                const fieldsMap: Record<number, WebApiModelsCustomListField[]> = {};
                results.forEach(result => {
                    fieldsMap[result.listId] = result.fields;
                });
                setCustomListFields(fieldsMap);
            });
        });
    }, []);

    const getCustomListName = (listId?: number | null) => {
        if (!listId) return '-';
        const list = customLists.find(l => l.id === listId);
        return list?.name || `List ${listId}`;
    };

    const getFieldName = (listId: number | null | undefined, fieldId: number | null | undefined) => {
        if (!listId || !fieldId) return '-';
        const fields = customListFields[listId];
        if (!fields) return `Field ${fieldId}`;
        const field = fields.find(f => f.id === fieldId);
        return field?.nameAdmin || `Field ${fieldId}`;
    };

    const renderMemberConnectionChanges = (record: WebApiCustomListsDtoCustomListHistoryDto) => {
        if (!record.memberConnectionChanges || record.memberConnectionChanges.length === 0) {
            return null;
        }

        return (
            <div style={{ marginTop: '10px' }}>
                <strong>{GetText("CustomList_MemberConnections") || "Member Connections"}:</strong>
                <ul style={{ marginTop: '5px', marginBottom: 0 }}>
                    {record.memberConnectionChanges.map((change, index) => (
                        <li key={index}>
                            {change.memberName || GetText("Common_Unknown")} - {change.action || GetText("Common_UnknownAction")}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const columns: ColumnsType<WebApiCustomListsDtoCustomListHistoryDto> = [
        {
            title: GetText("History_Date"),
            dataIndex: 'date',
            key: 'date',
            render: (_, history: WebApiCustomListsDtoCustomListHistoryDto) => (
                <span>
                    <b>{formatDateString(history.date!.toString())}</b>
                    <br />
                    <small>{history.createdByName || '-'}</small>
                </span>
            ),
        },
        {
            title: GetText("CustomList_List"),
            dataIndex: 'customListId',
            key: 'customListId',
            render: (_, history: WebApiCustomListsDtoCustomListHistoryDto) => (
                <span>{getCustomListName(history.customListId)}</span>
            ),
        },
        {
            title: GetText("CustomList_Item"),
            dataIndex: 'entityName',
            key: 'entityName',
            render: (_, history: WebApiCustomListsDtoCustomListHistoryDto) => (
                <span>{history.entityName || '-'}</span>
            ),
        },
        {
            title: GetText("History_EventType"),
            dataIndex: 'type',
            key: 'type',
            render: (_, history: WebApiCustomListsDtoCustomListHistoryDto) => (
                <span>{mapHistoryEventTypeToText(Number(history.type))}</span>
            ),
        },
    ];

    return (
        <LayoutLeftMenu title={GetText("CustomList_HistoryTitle")} menu={<LeftMenu selected={"customListHistory"}></LeftMenu>}>
            {user.subscriptionLevel == SubscriptionLevel.Free ? (
                <i>{GetText("Groups_UpgradeTo")} <a href='/Pricing'>Premium</a></i>
            ) : (
                <Table
                    columns={columns}
                    style={{ marginTop: "20px" }}
                    locale={{
                        emptyText: GetText("History_NoHistoryYet")
                    }}
                    className="admin-table"
                    rowKey="id"
                    dataSource={history}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div>
                                {record.changes && record.changes.length > 0 && (
                                    <table className='version-history-table'>
                                        <thead>
                                            <tr>
                                                <th>{GetText("History_Field")}</th>
                                                <th>{GetText("History_OldValue")}</th>
                                                <th>{GetText("History_NewValue")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {record.changes.map((change, index) => (
                                                <tr key={index}>
                                                    <td>{getFieldName(record.customListId, change.fieldId)}</td>
                                                    <td>{GetStringIfNotEmpty(change.oldValue)}</td>
                                                    <td>{GetStringIfNotEmpty(change.newValue)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                                {renderMemberConnectionChanges(record)}
                            </div>
                        ),
                        rowExpandable: (record) =>
                            !!(record.changes && record.changes.length > 0) ||
                            !!(record.memberConnectionChanges && record.memberConnectionChanges.length > 0),
                    }}
                />
            )}
        </LayoutLeftMenu>
    );
};

export default CustomListHistory;
